import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Globe2, GraduationCap, Users, Package, ArrowRight, Loader2, HeartHandshake, Recycle,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const IMG = "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function GlobalImpact() {
  const [data, setData] = useState(null);
  const [sponsorFor, setSponsorFor] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/impact/regions").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const submitSponsor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/impact/sponsor", { email, name, region: sponsorFor });
      toast.success(`Thank you—your pledge to ${sponsorFor} is recorded.`);
      setSponsorFor(null); setEmail(""); setName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Model explainer */}
      <section className="relative overflow-hidden bg-navy paper-grain text-white">
        <div className="absolute inset-0 opacity-15">
          <img src={IMG} alt="Global classroom" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/95 to-navy/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">The Model</span>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl mt-3 max-w-3xl leading-tight">
            Cross-Subsidization &amp; Localized Asset Distribution
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">
            Every premium SAT/AP subscription directly funds a <span className="text-white font-medium">non-monetary</span> academic
            asset network—books, digital devices, solar power packs, and direct stipend grants—for real-world educators
            teaching orphaned students in vulnerable communities.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: GraduationCap, t: "Premium tuition", d: "Scholars subscribe to the Cognitive Capital suite." },
              { icon: Recycle, t: "Cross-subsidy", d: "Margin is converted into physical & digital learning assets." },
              { icon: HeartHandshake, t: "Local delivery", d: "Vetted educators receive assets—never cash transfers abroad." },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }} className="rounded-xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center gap-2 text-persian-blue">
                  <s.icon className="h-5 w-5" />
                  <span className="text-[11px] font-mono uppercase tracking-widest">Step {i + 1}</span>
                </div>
                <h3 className="font-serif text-xl mt-2">{s.t}</h3>
                <p className="text-sm text-slate-300 mt-1 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Aggregate metrics */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { icon: Globe2, label: "Regions served", value: data.regions.length },
              { icon: Users, label: "Educators funded", value: data.total_educators },
              { icon: GraduationCap, label: "Students reached", value: data.total_students.toLocaleString() },
              { icon: Package, label: "Asset categories", value: 0 },
            ].map((m, i) => (
              <div key={i} data-testid={`impact-metric-${i}`}
                className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 text-center">
                <m.icon className="h-6 w-6 text-persian-blue mx-auto" />
                <div className="font-serif text-3xl font-bold text-navy mt-2">{m.value}</div>
                <div className="text-xs text-slate-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-8">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">The Network</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-2">Localized regional profiles</h2>
          <p className="text-slate-600 mt-2 max-w-2xl">Each region operates through vetted, on-the-ground educators receiving physical and digital assets—strictly non-monetary distribution.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data?.regions.map((r, i) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }} data-testid={`region-card-${r.id}`}
              className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col">
              <div className="p-6 flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {r.flag ? <span className="text-2xl leading-none">{r.flag}</span>
                        : <Globe2 className="h-6 w-6 text-persian-blue" />}
                      <h3 className="font-serif text-2xl font-semibold text-navy">{r.country}</h3>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{r.region} · {r.context}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{r.note}</p>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="rounded-lg bg-navy-soft p-3">
                    <div className="font-serif text-2xl font-bold text-navy">{r.educators}</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">Educators</div>
                  </div>
                  <div className="rounded-lg bg-navy-soft p-3">
                    <div className="font-serif text-2xl font-bold text-navy">{r.students.toLocaleString()}</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">Students</div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-persian-blue font-semibold mb-2">Asset delivery ledger</div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.assets.map((a) => (
                      <span key={a} className="text-xs bg-persian-blue/10 text-navy border border-persian-blue/30 rounded-full px-2.5 py-1">{a}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6">
                <Button onClick={() => setSponsorFor(r.region)} data-testid={`sponsor-teacher-button-${r.id}`}
                  className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
                  Sponsor a teacher in {r.region} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={!!sponsorFor} onOpenChange={(o) => !o && setSponsorFor(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="sponsor-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">Sponsor a teacher · {sponsorFor}</DialogTitle>
            <DialogDescription className="text-slate-500">
              Register your intent to endow an educator with non-monetary academic assets in {sponsorFor}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSponsor} className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Register your intent to endow an educator. Our team will follow up with the non-monetary asset options.</p>
            <div className="space-y-1.5">
              <Label htmlFor="s-name">Your name (optional)</Label>
              <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="sponsor-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-email">Email</Label>
              <Input id="s-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="sponsor-email-input" placeholder="you@email.com" />
            </div>
            <Button type="submit" disabled={loading} data-testid="sponsor-submit-button"
              className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm my pledge
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
